import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  BarChart3, 
  Download,
  Clock,
  DollarSign,
  Building2,
  Users,
  Filter,
  Sparkles,
  Wrench
} from 'lucide-react';
import { getLaborHoursReport, exportLaborHoursCSV, getWorkers } from '@/lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';

export const ReportsView = ({ selectedProperty, properties }) => {
  const [report, setReport] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    propertyId: selectedProperty?.id || 'all',
    workerId: 'all',
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getLaborHoursReport(
        filters.propertyId === 'all' ? null : filters.propertyId,
        filters.workerId === 'all' ? null : filters.workerId,
        filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : null,
        filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : null
      );
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await getWorkers(true);
      setWorkers(response.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = () => {
    const url = exportLaborHoursCSV(
      filters.propertyId === 'all' ? null : filters.propertyId,
      filters.workerId === 'all' ? null : filters.workerId,
      filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : null,
      filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : null
    );
    window.open(url, '_blank');
  };

  const setQuickRange = (range) => {
    const today = new Date();
    switch (range) {
      case 'today':
        setFilters(prev => ({ ...prev, startDate: today, endDate: today }));
        break;
      case 'week':
        setFilters(prev => ({ ...prev, startDate: subDays(today, 7), endDate: today }));
        break;
      case 'month':
        setFilters(prev => ({ ...prev, startDate: startOfMonth(today), endDate: endOfMonth(today) }));
        break;
      case 'quarter':
        setFilters(prev => ({ ...prev, startDate: subDays(today, 90), endDate: today }));
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Labor Hours Report
          </h1>
          <p className="text-slate-500 mt-1">
            Track labor hours and costs across properties and workers
          </p>
        </div>
        <Button 
          onClick={handleExport}
          variant="outline"
          data-testid="export-csv-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-100">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filters</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Property Filter */}
            <div className="space-y-1">
              <Label className="text-xs">Property</Label>
              <Select
                value={filters.propertyId}
                onValueChange={(value) => setFilters(prev => ({ ...prev, propertyId: value }))}
              >
                <SelectTrigger data-testid="report-property-filter">
                  <SelectValue placeholder="All Properties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Worker Filter */}
            <div className="space-y-1">
              <Label className="text-xs">Worker</Label>
              <Select
                value={filters.workerId}
                onValueChange={(value) => setFilters(prev => ({ ...prev, workerId: value }))}
              >
                <SelectTrigger data-testid="report-worker-filter">
                  <SelectValue placeholder="All Workers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Workers</SelectItem>
                  {workers.map(worker => (
                    <SelectItem key={worker.id} value={worker.id}>{worker.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="space-y-1">
              <Label className="text-xs">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal")}
                    data-testid="report-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.startDate ? format(filters.startDate, 'MMM d, yyyy') : 'Select'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.startDate}
                    onSelect={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal")}
                    data-testid="report-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.endDate ? format(filters.endDate, 'MMM d, yyyy') : 'Select'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.endDate}
                    onSelect={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Quick Range Buttons */}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setQuickRange('today')}>Today</Button>
            <Button variant="outline" size="sm" onClick={() => setQuickRange('week')}>Last 7 Days</Button>
            <Button variant="outline" size="sm" onClick={() => setQuickRange('month')}>This Month</Button>
            <Button variant="outline" size="sm" onClick={() => setQuickRange('quarter')}>Last 90 Days</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="spinner"></div>
        </div>
      ) : report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-white border border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Hours</p>
                    <p className="text-2xl font-bold text-slate-900 font-manrope">
                      {report.summary.total_hours.toFixed(1)}h
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Cost</p>
                    <p className="text-2xl font-bold text-emerald-600 font-manrope">
                      ${report.summary.total_cost.toFixed(2)}
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Cleaning Hours</p>
                    <p className="text-2xl font-bold text-slate-900 font-manrope">
                      {report.summary.cleaning_hours.toFixed(1)}h
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <Sparkles className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Maintenance Hours</p>
                    <p className="text-2xl font-bold text-slate-900 font-manrope">
                      {report.summary.maintenance_hours.toFixed(1)}h
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <Wrench className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* By Property Table */}
          <Card className="bg-white border border-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-manrope">
                <Building2 className="h-5 w-5" />
                Hours by Property
              </CardTitle>
            </CardHeader>
            <CardContent>
              {report.by_property.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Property</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Cleaning</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Maintenance</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Total Hours</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.by_property.map(prop => (
                        <tr key={prop.property_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium">{prop.property_name}</td>
                          <td className="py-3 px-4 text-right">{prop.cleaning_hours.toFixed(1)}h</td>
                          <td className="py-3 px-4 text-right">{prop.maintenance_hours.toFixed(1)}h</td>
                          <td className="py-3 px-4 text-right font-medium">{prop.total_hours.toFixed(1)}h</td>
                          <td className="py-3 px-4 text-right font-medium text-emerald-600">${prop.total_cost.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No data for selected filters</p>
              )}
            </CardContent>
          </Card>

          {/* By Worker Table */}
          <Card className="bg-white border border-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-manrope">
                <Users className="h-5 w-5" />
                Hours by Worker
              </CardTitle>
            </CardHeader>
            <CardContent>
              {report.by_worker.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Worker</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Cleaning</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Maintenance</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Total Hours</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Total Cost</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.by_worker.map(worker => (
                        <tr key={worker.worker_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium">{worker.worker_name}</td>
                          <td className="py-3 px-4 text-right">{worker.cleaning_hours.toFixed(1)}h</td>
                          <td className="py-3 px-4 text-right">{worker.maintenance_hours.toFixed(1)}h</td>
                          <td className="py-3 px-4 text-right font-medium">{worker.total_hours.toFixed(1)}h</td>
                          <td className="py-3 px-4 text-right font-medium text-emerald-600">${worker.total_cost.toFixed(2)}</td>
                          <td className="py-3 px-4 text-right">{worker.tasks_completed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No data for selected filters</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ReportsView;
